import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Table,
  Space,
  message,
  Modal,
  Input,
  InputNumber,
  Typography,
  Empty,
  Tag,
  Alert,
  Tooltip
} from 'antd';
import {
  VideoCameraOutlined,
  PlusOutlined,
  LinkOutlined,
  QrcodeOutlined,
  DeleteOutlined,
  EditOutlined,
  PlayCircleOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { QRCode } from 'antd';
import { TournamentService } from '@/services/tournament.service';
import { logger } from '@/utils/logger';
import type { Tournament } from '@/types/api.types';

const { Title } = Typography;

interface LiveStreamSetupProps {
  tournament: Tournament;
  onUpdate: () => void;
}

export const LiveStreamSetup: React.FC<LiveStreamSetupProps> = ({ tournament, onUpdate }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [controlTatamiModalOpen, setControlTatamiModalOpen] = useState(false);
  const [selectedTatami, setSelectedTatami] = useState<number>(1);
  const [selectedUrl, setSelectedUrl] = useState<string>('');
  const [editingTatami, setEditingTatami] = useState<string | null>(null);
  const [editingUrl, setEditingUrl] = useState('');
  const [formTatami, setFormTatami] = useState<number>(1);
  const [formUrl, setFormUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const streamLinks = tournament.streamLinks ?? {};
  const rows = Object.entries(streamLinks)
    .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
    .map(([tatami, url]) => ({ tatami, url }));

  const saveStreamLinks = async (next: Record<string, string>) => {
    if (!id) return;
    setLoading(true);
    try {
      await TournamentService.updateStreamLinks(Number(id), Object.keys(next).length ? next : null);
      message.success('Ссылки на трансляции обновлены');
      onUpdate();
      setAddModalOpen(false);
      setEditModalOpen(false);
      setFormUrl('');
      setFormTatami(1);
      setEditingTatami(null);
      setEditingUrl('');
    } catch (e) {
      logger.error('Failed to update stream links', e);
      message.error('Не удалось сохранить ссылки на трансляции');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    if (!formUrl.trim()) {
      message.warning('Введите ссылку на трансляцию');
      return;
    }
    const next = { ...streamLinks, [String(formTatami)]: formUrl.trim() };
    saveStreamLinks(next);
  };

  const handleEdit = () => {
    if (!editingTatami || !editingUrl.trim()) return;
    const next = { ...streamLinks };
    delete next[editingTatami];
    next[String(formTatami)] = editingUrl.trim();
    saveStreamLinks(next);
  };

  const handleDelete = (tatami: string) => {
    const next = { ...streamLinks };
    delete next[tatami];
    saveStreamLinks(next);
  };

  const openEdit = (tatami: string, url: string) => {
    setEditingTatami(tatami);
    setEditingUrl(url);
    setFormTatami(parseInt(tatami, 10) || 1);
    setEditModalOpen(true);
  };

  const openQr = (url: string) => {
    setSelectedUrl(url);
    setQrModalOpen(true);
  };

  const handleOpenControl = () => {
    setControlTatamiModalOpen(true);
  };

  const handleControlTatamiOk = () => {
    setControlTatamiModalOpen(false);
    navigate(`/tournaments/${id}/broadcast/control?tatami=${selectedTatami}`);
  };

  const getOverlayUrl = (tatami: string) =>
    id ? `${window.location.origin}/tournaments/${id}/broadcast?tatami=${tatami}` : '';

  const copyOverlayUrl = (tatami: string) => {
    const url = getOverlayUrl(tatami);
    if (url && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => message.success('Ссылка скопирована'));
    }
  };

  return (
    <>
      <Card>
        <Title level={4}>
          <VideoCameraOutlined style={{ marginRight: 8 }} />
          Настройка трансляций
        </Title>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Оверлей в OBS"
          description="Чтобы показывать на трансляции таймер и данные боя: в OBS добавьте источник «Браузер» (Browser Source) и вставьте ссылку оверлея для нужного татами (кнопка копирования в таблице)."
        />

        <Space style={{ marginBottom: 16 }} wrap>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
            Добавить трансляцию
          </Button>
          <Button icon={<PlayCircleOutlined />} onClick={handleOpenControl}>
            Открыть управление трансляцией
          </Button>
        </Space>

        {rows.length === 0 ? (
          <Empty
            description="Нет запланированных трансляций"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ margin: '32px 0' }}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
              Добавить трансляцию
            </Button>
          </Empty>
        ) : (
          <Table
            dataSource={rows}
            rowKey="tatami"
            pagination={false}
            columns={[
              {
                title: 'Татами',
                dataIndex: 'tatami',
                key: 'tatami',
                width: 100,
                render: (t: string) => <Tag color="blue">Татами {t}</Tag>
              },
              {
                title: 'Ссылка',
                dataIndex: 'url',
                key: 'url',
                ellipsis: true,
                render: (url: string) => (
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    {url.length > 60 ? `${url.slice(0, 57)}...` : url}
                  </a>
                )
              },
              {
                title: 'Оверлей для OBS',
                key: 'overlay',
                width: 120,
                render: (_, r) => {
                  const overlayUrl = getOverlayUrl(r.tatami);
                  return (
                    <Tooltip title="Скопировать ссылку для OBS (Browser Source)">
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() => copyOverlayUrl(r.tatami)}
                        disabled={!overlayUrl}
                      >
                        Копировать
                      </Button>
                    </Tooltip>
                  );
                }
              },
              {
                title: 'QR‑код',
                key: 'qr',
                width: 100,
                render: (_, r) => (
                  <Button type="text" icon={<QrcodeOutlined />} onClick={() => openQr(r.url)}>
                    QR
                  </Button>
                )
              },
              {
                title: 'Действия',
                key: 'actions',
                width: 140,
                render: (_, r) => (
                  <Space>
                    <Button
                      type="link"
                      size="small"
                      icon={<LinkOutlined />}
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Открыть
                    </Button>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(r.tatami, r.url)}>
                      Изменить
                    </Button>
                    <Button
                      type="link"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDelete(r.tatami)}
                    />
                  </Space>
                )
              }
            ]}
          />
        )}
      </Card>

      <Modal
        title="Добавить трансляцию"
        open={addModalOpen}
        onOk={handleAdd}
        onCancel={() => {
          setAddModalOpen(false);
          setFormUrl('');
          setFormTatami(1);
        }}
        okText="Добавить"
        cancelText="Отмена"
        confirmLoading={loading}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <div style={{ marginBottom: 4 }}>Татами</div>
            <InputNumber
              min={1}
              max={20}
              value={formTatami}
              onChange={(v) => setFormTatami(v ?? 1)}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4 }}>Ссылка (vkvideo.ru, YouTube и др.)</div>
            <Input
              placeholder="https://vkvideo.ru/..."
              value={formUrl}
              onChange={(e) => setFormUrl(e.target.value)}
            />
          </div>
        </Space>
      </Modal>

      <Modal
        title="Изменить трансляцию"
        open={editModalOpen}
        onOk={handleEdit}
        onCancel={() => {
          setEditModalOpen(false);
          setEditingTatami(null);
          setEditingUrl('');
        }}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={loading}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <div style={{ marginBottom: 4 }}>Татами</div>
            <InputNumber
              min={1}
              max={20}
              value={formTatami}
              onChange={(v) => setFormTatami(v ?? 1)}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4 }}>Ссылка</div>
            <Input
              placeholder="https://vkvideo.ru/..."
              value={editingUrl}
              onChange={(e) => setEditingUrl(e.target.value)}
            />
          </div>
        </Space>
      </Modal>

      <Modal
        title="QR‑код трансляции"
        open={qrModalOpen}
        onCancel={() => setQrModalOpen(false)}
        footer={null}
      >
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          {selectedUrl && <QRCode value={selectedUrl} size={200} />}
          <div style={{ marginTop: 16, wordBreak: 'break-all', fontSize: 12, color: '#666' }}>
            {selectedUrl}
          </div>
        </div>
      </Modal>

      <Modal
        title="Выберите татами для управления трансляцией"
        open={controlTatamiModalOpen}
        onOk={handleControlTatamiOk}
        onCancel={() => setControlTatamiModalOpen(false)}
        okText="Перейти к управлению"
        cancelText="Отмена"
      >
        <div>
          <div style={{ marginBottom: 8 }}>Номер татами:</div>
          <InputNumber
            min={1}
            max={20}
            value={selectedTatami}
            onChange={(v) => setSelectedTatami(v ?? 1)}
            style={{ width: '100%' }}
          />
        </div>
      </Modal>
    </>
  );
};
